<template>
  <div id="partner-order-table-container">
    <div class="po-header-container">
      <SACTableSearch
        :search="partnerOrderTableSearch"
        @input="changeSearch"
      />
    </div>
    <div class="po-container">
      <v-data-table
        :headers="headers"
        :items="poTableData"
        :loading="loadingPartnerOrder"
        :options="partnerOrderTableOptions"
        :search="partnerOrderTableSearch"
        height="700px"
        fixed-header
        hide-default-footer
      >
        <template #[`item.orderNumber`]="{ item }">
          <td>
            <div class="flex-row align-center">
              <span
                v-if="determineOrderStatusDot(item) !== 'error'
                  && determineOrderStatusDot(item) !== 'cancelled'"
                :class="[determineOrderStatusDot(item), 'dot']"
              />
              <span
                v-else-if="determineOrderStatusDot(item) === 'error'"
                class="error-status"
              >
                !
              </span>
              <span
                v-else-if="determineOrderStatusDot(item) === 'cancelled'"
                class="cancelled-status"
              >
                -
              </span>
              <router-link
                :to="`/order/partner/${item.orderNumber}/review`"
              >
                {{ item.orderNumber }}
              </router-link>
            </div>
          </td>
        </template>
        <template #[`item.status`]="{ item }">
          <td>
            <span :class="[determineOrderStatus(item.status)]">
              {{ item.status }}
            </span>
          </td>
        </template>
        <template #[`item.orderDate`]="{ item }">
          <td>
            {{ customDateFilter(item.orderDate, dateFormat) }}
          </td>
        </template>
        <template #[`item.shipDate`]="{ item }">
          <td>
            {{ customDateFilter(item.shipDate, dateFormat) }}
          </td>
        </template>
        <template #[`item.deliveryDate`]="{ item }">
          <td>
            {{ customDateFilter(item.deliveryDate, dateFormat) }}
          </td>
        </template>
        <template #[`item.trackingNumber`]="{ item }">
          <td>
            <a
              v-if="item.trackingNumber"
              :href="createTrackingLink(item)"
              target="_blank"
              rel="noopener noreferer"
            >
              {{ item.trackingNumber }}
            </a>
            <span v-else>
              --
            </span>
          </td>
        </template>
        <template #[`item.orderTotal`]="{ item }">
          <td>
            {{ `$${Math.round(item.orderTotal)}` }}
          </td>
        </template>
      </v-data-table>
    </div>
    <SACTableFooter
      :totalRecords="poTableData.length"
      :rowCount="partnerOrderTableOptions.itemsPerPage"
      :changeRowCount="changeRowCount"
      :page="partnerOrderTableOptions.page"
      :totalPage="poTableRecordCount"
      :changePage="changePage"
      :loading="loadingPartnerOrder"
    />
  </div>
</template>

<script>
import SACTableSearch from '@shared/components/table/SACTableSearch.vue';
import SACTableFooter from '@shared/components/table/SACTableFooter.vue';

import partnerOrderMixin from '@shared/mixins/partnerOrderMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';

export default {
  name: 'PartnerOrderTable',
  components: {
    SACTableSearch,
    SACTableFooter,
  },
  mixins: [partnerOrderMixin, unitPreferencesMixin],
  data() {
    const headers = [{
      text: 'Order Number',
      align: 'start',
      sortable: true,
      filterable: true,
      value: 'orderNumber',
    }, {
      text: 'Status',
      align: 'start',
      sortable: true,
      filterable: true,
      value: 'status',
    }, {
      text: 'Order Date',
      align: 'start',
      sortable: true,
      filterable: true,
      value: 'orderDate',
    }, {
      text: 'Ship Date',
      align: 'start',
      sortable: true,
      filterable: true,
      value: 'shipDate',
    }, {
      text: 'Delivery Date',
      align: 'start',
      sortable: true,
      filterable: true,
      value: 'deliveryDate',
    }];

    if (this.corporation?.parent) {
      headers.push({
        text: 'Branch',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'corpName',
      });
    }

    headers.push({
      text: 'Total',
      align: 'start',
      sortable: true,
      filterable: true,
      value: 'orderTotal',
    });
    return {
      headers,
    };
  },
  methods: {
    changePage(page) {
      this.$store.dispatch('changePartnerOrderTableOption', {
        field: 'page',
        value: page,
      });
    },
    changeRowCount(rowCount) {
      this.$store.dispatch('changePartnerOrderTableOption', {
        field: 'itemsPerPage',
        value: rowCount,
      });
    },
    changeSort([item]) {
      this.$store.dispatch('changePartnerOrderTableOption', {
        field: 'sortBy',
        value: [item],
      });
    },
    changeSearch(value) {
      this.$store.dispatch('changePartnerOrderValue', {
        field: 'partnerOrderTableSearch',
        value,
      });
    },
  },
};
</script>

<style lang="scss">

#partner-order-table-container {
  @include table-container;
  .po-header-container {
    display: grid;
    justify-items: flex-end;
    margin-bottom: 18px;
  }
  .po-container {
    @include table-body;
    .v-data-table__wrapper {
      height: 700px;
      @include v-table-header;
      @include v-table-body;
    }
  }
  .green-dot {
    background-color: $BASIL100;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    margin-right: 12px;
  }
  .red-dot {
    background-color: $TOMATO100;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    margin-right: 12px;
  }
  .yellow-dot {
    background-color: $SUNSET100;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    margin-right: 12px;
  }
  .error-status {
    width: 8px;
    margin-right: 12px;
    text-align: center;
    color: $TOMATO100;
    font-weight: 700;
    font-size: 1rem;
    transform: scale(1.2);
  }
  .cancelled-status {
    width: 8px;
    margin-right: 12px;
    text-align: center;
    color: $BLACK;
    font-weight: 700;
    font-size: 1rem;
    transform: scale(1.2);
  }
}
</style>
